import React, { useMemo, useRef, useState } from "react";
import type { MemberProduct } from "@/types/memberProducts";
import {
  bundledProductsOf,
  isBundleProductPage,
  isDeviceSubscriptionProduct,
} from "@/types/product";
import { CartPlusIcon } from "@/components/icons";
import { useLocalizedTexts, t } from "@/utils/translations";
import { useProduct } from "@/hooks/useProduct";
import { useCart } from "@/hooks/useCart";
import { useIsB2b } from "@/hooks/useIsB2b";
import { DiscountedUnitPrice } from "@/components/common/DiscountedUnitPrice/DiscountedUnitPrice";
import { isProductDiscounted } from "@/utils/discount";
import { trackAddToCart } from "@/components/analytics/gtm-events";
import { useAnalyticsIdentity } from "@/components/analytics/AnalyticsIdentityContext";
import { addDeviceSubscriptionToCart } from "@/api/memberProducts";
import { DeviceSubscriptionPicker } from "./DeviceSubscriptionPicker";
import { BundleContents } from "./BundleContents";
import { BundleOffers } from "./BundleOffers";
import { VariantSelector } from "./VariantSelector";
import { StickyCartBar } from "./StickyCartBar";
import { StockIndicator } from "@/components/common/StockIndicator/StockIndicator";
import { splitOnFirstBreak } from "@/utils/text";
import { sanitizeHtml } from "@/utils/sanitizeHtml";
import styles from "./ProductDetails.module.css";

const MAX_QUANTITY = 99;

function Tagline({ description }: { description: string }) {
  const localizedTexts = useLocalizedTexts();
  const [expanded, setExpanded] = useState(false);
  const { first, rest } = useMemo(
    () => splitOnFirstBreak(description),
    [description]
  );

  if (!description) return null;

  if (!rest) {
    return (
      <div
        className={styles.tagline}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
      />
    );
  }

  return (
    <div className={styles.tagline}>
      <div
        dangerouslySetInnerHTML={{
          __html: sanitizeHtml(expanded ? description : first),
        }}
      />
      <button
        type="button"
        className={styles.readMore}
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        {expanded
          ? t(localizedTexts, "Tracker.Products.ShowLess", "Show less")
          : t(localizedTexts, "Tracker.Products.ReadMore", "Read more")}
      </button>
    </div>
  );
}

function FeatureBadges({ features }: { features: string[] }) {
  if (!features.length) return null;
  return (
    <div className={styles.featureBadges}>
      {features.map((feature, index) => (
        <span key={index} className={styles.badge}>
          <span>{feature.trim()}</span>
        </span>
      ))}
    </div>
  );
}

export const ProductDetails: React.FC = () => {
  const [quantity, setQuantity] = useState(1);
  const [selectedImei, setSelectedImei] = useState<string | null>(null);
  const [selectedDeviceName, setSelectedDeviceName] = useState<string | null>(
    null
  );
  const [isAddingDeviceSubscription, setIsAddingDeviceSubscription] =
    useState(false);
  const [deviceSubscriptionError, setDeviceSubscriptionError] = useState<
    string | null
  >(null);
  const localizedTexts = useLocalizedTexts();
  const {
    productData,
    variants,
    requiresVariantSelection,
    selectedVariant,
    selectVariant,
  } = useProduct();
  const {
    addToCart,
    addBundleToCart,
    isAddingToCart,
    refreshCart,
    setDrawerOpen,
  } = useCart();
  const mainAddToCartRef = useRef<HTMLButtonElement>(null);

  // Chosen variant id per bundled product, for bundle items sold in versions.
  const [bundleVariants, setBundleVariants] = useState<Record<string, string>>(
    {}
  );

  const productContent = productData.compositions?.productContent;
  const productName = productData.name ?? "Product";
  const sellingLine = productContent?.sellingLine ?? "";
  const description = productContent?.description ?? "";
  const features = productContent?.features ?? [];
  const isB2b = useIsB2b();
  const identity = useAnalyticsIdentity();
  const priceInfo = productData.compositions?.product?.price;
  const price = priceInfo?.formattedCalculatedPrice ?? "";
  const productSku = productData.compositions?.product?.sku;
  const isDeviceSubscription = isDeviceSubscriptionProduct(productData);

  // A product sold in versions keeps its stock per variant, and the product node
  // itself carries a figure that means nothing for it (each variant is its own
  // item in Business Central). So: show the chosen version's band, and nothing
  // at all until one is chosen — never the product-level band as a stand-in.
  const stockLevel =
    variants.length > 0
      ? (selectedVariant?.stockLevel ?? null)
      : (productData.compositions?.product?.stockLevel ?? null);

  // A bundle has no SKU: buying it adds one cart line per included product, via
  // its own endpoint. Posting it to the ordinary add-to-cart is rejected.
  const isBundle = isBundleProductPage(productData);
  const bundleItems = useMemo(
    () => (isBundle ? bundledProductsOf(productData) : []),
    [isBundle, productData]
  );
  // Every bundled product sold in versions must have one picked, or its order
  // line would carry no size/colour.
  const bundleItemsNeedingVariant = useMemo(
    () => bundleItems.filter((item) => (item.variants?.length ?? 0) > 0),
    [bundleItems]
  );
  const bundleVariantsComplete = bundleItemsNeedingVariant.every(
    (item) => !!bundleVariants[item.id]
  );

  // Bundle deals that include THIS product, advertised as links to their own pages.
  const bundleOffers = productContent?.bundles?.items ?? [];

  const keyPoints = productContent?.keyPoints ?? [];
  const note = productContent?.note?.trim() ?? "";

  const productReference = productData.compositions?.product?.productReference;

  const handleDeviceSelect = (imei: string, device: MemberProduct) => {
    setSelectedImei(imei);
    setSelectedDeviceName(device.name);
    setDeviceSubscriptionError(null);
  };

  const handleAddDeviceSubscription = async () => {
    if (isAddingDeviceSubscription || !productSku || !selectedImei) {
      return;
    }
    setIsAddingDeviceSubscription(true);
    setDeviceSubscriptionError(null);
    const result = await addDeviceSubscriptionToCart({
      productSku,
      deviceId: selectedImei,
      imei: selectedImei,
    });
    setIsAddingDeviceSubscription(false);
    if (!result.ok) {
      setDeviceSubscriptionError(result.error);
      return;
    }
    await refreshCart();
    setDrawerOpen(true);

    const productPrice =
      productData.compositions?.product?.price?.calculatedPrice ?? 0;
    trackAddToCart({
      product: productData,
      currency: productData.compositions?.product?.price?.currency ?? "",
      price: productPrice,
      value: productPrice,
      quantity: 1,
      identity,
    });
  };

  const handleAddBundleToCart = async () => {
    if (isAddingToCart || !bundleVariantsComplete) return;

    const variants = bundleItemsNeedingVariant.flatMap((item) => {
      const variantId = bundleVariants[item.id];
      return variantId ? [{ productId: item.id, variantId }] : [];
    });

    const added = await addBundleToCart(productData.id, quantity, variants);
    if (!added) return;

    const bundlePrice = productData.compositions?.product?.price;
    const price = bundlePrice?.calculatedPrice ?? 0;
    trackAddToCart({
      product: productData,
      currency: bundlePrice?.currency ?? "",
      price,
      value: price * quantity,
      quantity,
      identity,
    });
  };

  const handleAddToCart = async () => {
    if (isDeviceSubscription) {
      await handleAddDeviceSubscription();
      return;
    }
    if (isBundle) {
      await handleAddBundleToCart();
      return;
    }
    if (isAddingToCart || !productReference) return;
    // A product sold in several versions must never reach the cart without one:
    // the order line would carry no size/colour.
    if (requiresVariantSelection && !selectedVariant) return;
    await addToCart(productReference, quantity, selectedVariant?.id);

    const productPrice =
      productData.compositions?.product?.price?.calculatedPrice ?? 0;
    trackAddToCart({
      product: productData,
      currency: productData.compositions?.product?.price?.currency ?? "",
      price: productPrice,
      value: productPrice * quantity,
      quantity,
      variantSku: selectedVariant?.sku ?? undefined,
      identity,
    });
  };

  const addToCartDisabled = isDeviceSubscription
    ? isAddingDeviceSubscription || !selectedImei
    : isBundle
      ? isAddingToCart || bundleItems.length === 0 || !bundleVariantsComplete
      : isAddingToCart || (requiresVariantSelection && !selectedVariant);

  const decrementQty = () => setQuantity((q) => Math.max(1, q - 1));
  const incrementQty = () => setQuantity((q) => Math.min(MAX_QUANTITY, q + 1));

  return (
    <aside className={styles.infoColumn} data-component="product-details">
      <div className={styles.titleBlock}>
        {sellingLine && <span className={styles.brandLine}>{sellingLine}</span>}
        <h1 className={styles.productTitle}>{productName}</h1>
      </div>

      <Tagline description={description} />

      {isB2b &&
      priceInfo &&
      (priceInfo.formattedListPrice ||
        priceInfo.formattedCalculatedPriceWithoutVat) ? (
        <div className={styles.priceRow}>
          <DiscountedUnitPrice
            variant="b2b"
            standardInclVat={priceInfo.formattedListPrice}
            accentPrice={priceInfo.formattedCalculatedPriceWithoutVat}
            size="lg"
            onDark
          />
        </div>
      ) : !isB2b &&
        isProductDiscounted(priceInfo) &&
        priceInfo?.formattedListPrice &&
        priceInfo?.formattedCalculatedPrice ? (
        <div className={styles.priceRow}>
          <DiscountedUnitPrice
            variant="discount"
            standardInclVat={priceInfo.formattedListPrice}
            accentPrice={priceInfo.formattedCalculatedPrice}
            standardLabel={t(
              localizedTexts,
              "Tracker.Products.IncludingVAT",
              "incl. VAT"
            )}
            netLabel={t(
              localizedTexts,
              "Tracker.Products.IncludingVAT",
              "incl. VAT"
            )}
            size="lg"
            onDark
          />
        </div>
      ) : (
        price && (
          <div className={styles.priceRow}>
            <span className={styles.price}>
              {price}
              <span className={styles.vat}>
                {t(
                  localizedTexts,
                  "Tracker.Products.IncludingVAT",
                  "incl. VAT"
                )}
              </span>
            </span>
          </div>
        )
      )}

      <StockIndicator level={stockLevel} surface="productPage" />

      <FeatureBadges features={features} />

      {isBundle && (
        <BundleContents
          items={bundleItems}
          selectedVariants={bundleVariants}
          onSelectVariant={(productId, variantId) =>
            setBundleVariants((prev) => ({ ...prev, [productId]: variantId }))
          }
          includesLabel={productData.includesLabel}
        />
      )}

      {!isDeviceSubscription && !isBundle && variants.length > 0 && (
        <VariantSelector
          variants={variants}
          selectedVariant={selectedVariant}
          onSelect={selectVariant}
        />
      )}

      {isDeviceSubscription && (
        <DeviceSubscriptionPicker
          selectedImei={selectedImei}
          onSelectionChange={handleDeviceSelect}
        />
      )}

      {deviceSubscriptionError && (
        <p className={styles.errorMessage} role="alert">
          {t(
            localizedTexts,
            "Tracker.Products.DeviceSubscription.AddToCartError",
            "Could not add the subscription to your cart. Please try again."
          )}
        </p>
      )}

      <div className={styles.qtyCartRow}>
        {!isDeviceSubscription && (
          <div
            className={styles.qty}
            aria-label={t(
              localizedTexts,
              "Tracker.Products.Quantity",
              "Quantity"
            )}
          >
            <button
              type="button"
              className={styles.qtyButton}
              onClick={decrementQty}
              aria-label={t(
                localizedTexts,
                "Tracker.Products.Decrease",
                "Decrease"
              )}
              disabled={quantity <= 1}
            >
              −
            </button>
            <input
              type="number"
              className={styles.qtyInput}
              value={quantity}
              min={1}
              max={MAX_QUANTITY}
              onChange={(e) => {
                const v = parseInt(e.target.value || "1", 10);
                const safe = Number.isNaN(v) ? 1 : v;
                setQuantity(Math.max(1, Math.min(safe, MAX_QUANTITY)));
              }}
            />
            <button
              type="button"
              className={styles.qtyButton}
              onClick={incrementQty}
              aria-label={t(
                localizedTexts,
                "Tracker.Products.Increase",
                "Increase"
              )}
              disabled={quantity >= MAX_QUANTITY}
            >
              +
            </button>
          </div>
        )}
        <button
          ref={mainAddToCartRef}
          type="button"
          className={styles.addToCartButton}
          onClick={handleAddToCart}
          disabled={addToCartDisabled}
        >
          <CartPlusIcon />
          <span>
            {isDeviceSubscription && selectedDeviceName
              ? `${t(localizedTexts, "Tracker.Products.AddToCart", "Add to cart")} (${selectedDeviceName})`
              : t(localizedTexts, "Tracker.Products.AddToCart", "Add to cart")}
          </span>
        </button>
      </div>

      {!isBundle && bundleOffers.length > 0 && (
        <BundleOffers
          items={bundleOffers}
          bundleLabel={productContent?.bundles?.bundleLabel}
        />
      )}

      {keyPoints.length > 0 && (
        <ul className={styles.trustList}>
          {keyPoints.map((item) => (
            <li key={item.id} className={styles.trustItem}>
              <span className={styles.trustIcon} data-icon={item.icon} />
              <span>{item.text}</span>
            </li>
          ))}
        </ul>
      )}

      {note && (
        <div className={styles.subscriptionNote}>
          <strong>
            {t(localizedTexts, "Tracker.Products.NoteLabel", "Note:")}
          </strong>{" "}
          {note}
        </div>
      )}

      <StickyCartBar mainButtonRef={mainAddToCartRef} quantity={quantity} />
    </aside>
  );
};

export default ProductDetails;
